import { useAppSelector } from 'redux/hooks';
import {
  selectActiveOrganization,
  selectContextOrganizationId,
  selectIsOnSubdomain,
  selectIsPublisher,
  selectIsUserLoggedIn
} from 'redux/auth';
import { exists } from 'lib/types';
import LoadingState from 'components/LoadingState';
import { MAIN_ROUTES } from 'router/routes';
import { Alert } from 'lib/components/Alert';
import { useAppParams } from 'lib/frontend/utils/router';
import useAsyncEffect from 'lib/frontend/hooks/useAsyncEffect';
import { getFirebaseContext } from 'utils/firebase';
import { getModelFromId, getModelFromSnapshot } from 'lib/model';
import { ColumnService } from 'lib/services/directory';
import { OrganizationModel } from 'lib/model/objects/organizationModel';
import { usePlacementAnonymousLogin } from 'routes/placeScroll/hooks/usePlacementAnonymousLogin';
import PublicationScheduleCalendar from './PublicationScheduleCalendar';

// TODO: Allow searching for publishers / anonymous user support
// TODO: Select products
// TODO: Select publishing mediums

export default function Schedule() {
  const { organizationId } = useAppParams(MAIN_ROUTES.SCHEDULE);
  const isOnSubdomain = useAppSelector(selectIsOnSubdomain);
  const userIsPublisher = useAppSelector(selectIsPublisher);
  const orgContextId = useAppSelector(selectContextOrganizationId);
  const activeOrganization = useAppSelector(selectActiveOrganization);
  const activeOrganizationModel = exists(activeOrganization)
    ? getModelFromSnapshot(
        OrganizationModel,
        getFirebaseContext(),
        activeOrganization
      )
    : null;
  const defaultOrganizationId =
    isOnSubdomain && !userIsPublisher ? orgContextId : organizationId;

  usePlacementAnonymousLogin();
  const isUserLoggedIn = useAppSelector(selectIsUserLoggedIn);

  const { value: organizationFromRoute, isError } = useAsyncEffect({
    fetchData: async () => {
      if (!defaultOrganizationId || !isUserLoggedIn) {
        return null;
      }

      return await getModelFromId(
        OrganizationModel,
        getFirebaseContext(),
        getFirebaseContext().organizationsRef(),
        defaultOrganizationId
      );
    },
    dependencies: [defaultOrganizationId, isUserLoggedIn],
    errorConfig: {
      service: ColumnService.WEB_PLACEMENT,
      message: 'Error loading publication schedule calendar',
      tags: {
        organizationId: defaultOrganizationId || ''
      }
    }
  });

  const organization = organizationFromRoute || activeOrganizationModel;

  if (isError) {
    return (
      <Alert
        id="schedule-error"
        status="error"
        title="Error loading publication schedule calendar. Please try again."
      />
    );
  }

  if (!organization) {
    return <LoadingState />;
  }

  if (!organization.isPublisherOrganization) {
    return (
      <Alert
        id="schedule-error"
        status="info"
        title="The selected organization is not a publisher. Please try again."
      />
    );
  }

  return <PublicationScheduleCalendar publisherOrganization={organization} />;
}
